/**
 * Remotion Overlay Renderer
 *
 * Drop-in replacement for the Fabric.js OverlayRenderer.
 * Uses Remotion Player to render overlays with WYSIWYG preview.
 *
 * Usage:
 *   Replace: <OverlayRenderer overlays={overlays} ... />
 *   With:    <RemotionOverlayRenderer overlays={overlays} ... />
 */

import React, { useMemo } from 'react';
import { Player, Thumbnail } from '@remotion/player';
import { RemotionSlide, RemotionSlideProps } from './compositions/RemotionSlide';
import { AnyOverlay, ImageOverlay, ImageFilter } from '../models/Post';
import { WATERMARK_STORAGE_PATH, WATERMARK_PUBLIC_URL } from '../constants/watermark';

export interface RemotionOverlayRendererProps {
  /** Array of overlays to render */
  overlays: AnyOverlay[];
  /** Container width in pixels */
  containerWidth: number;
  /** Container height in pixels */
  containerHeight: number;
  /** Background color */
  backgroundColor?: string;
  /** Background image URL */
  backgroundImageUrl?: string;
  /** Visual filter applied to the background image (CSS) */
  backgroundFilter?: ImageFilter;
  /** Map of overlay filePath -> signed URL (for images) */
  imageUrls?: Record<string, string>;
  /** Current time in seconds (for video playback) */
  currentTime?: number;
  /** Whether this is a video (enables timing/animation) */
  isVideo?: boolean;
  /** Video duration in seconds */
  videoDuration?: number;
  /** Whether video is playing */
  isPlaying?: boolean;
  /** Frames per second */
  fps?: number;
  /** Optional className for the container */
  className?: string;
  /** Optional style for the container */
  style?: React.CSSProperties;
  /** Reference media width for scaling (original/natural width) */
  referenceWidth?: number;
  /** Reference media height for scaling (original/natural height) */
  referenceHeight?: number;
  /**
   * Layout engine determines CSS compensation behavior.
   * - undefined/'fabric': Old posts - apply compensations for Fabric.js compatibility
   * - 'remotion': New posts - use clean CSS, no compensations
   *
   * Pass from MediaItem.metadata.layoutEngine. See Post.ts for details.
   */
  layoutEngine?: 'fabric' | 'remotion';
  /** Whether the user is on a free plan */
  isFreePlan?: boolean;
  /** Whether the post was generated with a watermark flag */
  wasGeneratedWithWatermark?: boolean;
}

export const RemotionOverlayRenderer: React.FC<RemotionOverlayRendererProps> = ({
  overlays,
  containerWidth,
  containerHeight,
  backgroundColor = '#000000',
  backgroundImageUrl,
  backgroundFilter,
  imageUrls = {},
  currentTime = 0,
  isVideo = false,
  videoDuration = 0,
  isPlaying = false,
  fps = 30,
  className,
  style,
  referenceWidth,
  referenceHeight,
  layoutEngine,
  isFreePlan = false,
  wasGeneratedWithWatermark = false,
}) => {
  // Round dimensions to integers (Remotion Player requirement)
  const roundedWidth = Math.round(containerWidth);
  const roundedHeight = Math.round(containerHeight);

  // Inject watermark overlay for free-plan users on watermarked posts —
  // the SAME PNG the server composites on export, so preview matches output.
  const showWatermark = isFreePlan && wasGeneratedWithWatermark;
  const effectiveOverlays = useMemo(() => {
    if (!showWatermark) return overlays;

    const watermarkOverlay: ImageOverlay = {
      id: '__watermark__',
      type: 'image',
      filePath: WATERMARK_STORAGE_PATH,
      position: { x: 39, y: 95 },
      width: 22,
      opacity: 100,
      zIndex: 9999,
      objectFit: 'contain',
    };

    return [...overlays, watermarkOverlay];
  }, [overlays, showWatermark]);

  // Resolve the watermark PNG via its public URL (no storage rules involved)
  const effectiveImageUrls = useMemo(
    () =>
      showWatermark
        ? { ...imageUrls, [WATERMARK_STORAGE_PATH]: WATERMARK_PUBLIC_URL }
        : imageUrls,
    [imageUrls, showWatermark]
  );

  // Build composition props
  const compositionProps: RemotionSlideProps = useMemo(
    () => ({
      width: roundedWidth,
      height: roundedHeight,
      backgroundColor,
      backgroundImageUrl,
      backgroundFilter,
      overlays: effectiveOverlays,
      imageUrls: effectiveImageUrls,
      currentTimeSeconds: currentTime,
      isVideo,
      videoDuration,
      fps,
      referenceWidth,
      referenceHeight,
      layoutEngine,
    }),
    [
      roundedWidth,
      roundedHeight,
      backgroundColor,
      backgroundImageUrl,
      backgroundFilter,
      effectiveOverlays,
      effectiveImageUrls,
      currentTime,
      isVideo,
      videoDuration,
      fps,
      referenceWidth,
      referenceHeight,
      layoutEngine,
    ]
  );

  // Calculate duration in frames
  const durationInFrames = isVideo ? Math.max(1, Math.ceil(videoDuration * fps)) : 1;

  // Calculate current frame from time
  const initialFrame = isVideo ? Math.min(Math.floor(currentTime * fps), Math.max(0, durationInFrames - 1)) : 0;

  // Don't render if dimensions are invalid
  if (!roundedWidth || !roundedHeight || roundedWidth <= 0 || roundedHeight <= 0) {
    return null;
  }

  return (
    <div
      className={className}
      style={{
        width: containerWidth,
        height: containerHeight,
        ...style
      }}
    >
      {isVideo ? (
        <Player
          component={RemotionSlide}
          inputProps={compositionProps}
          durationInFrames={durationInFrames}
          fps={fps}
          compositionWidth={roundedWidth}
          compositionHeight={roundedHeight}
          style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
          controls={false}
          autoPlay={isPlaying}
          loop={false}
          initialFrame={initialFrame}
          // This composition draws overlays only; media audio is owned by the
          // surrounding video player. Do not allocate silent audio players.
          numberOfSharedAudioTags={0}
          renderLoading={() => null}
          allowFullscreen={false}
        />
      ) : (
        <Thumbnail
          component={RemotionSlide}
          inputProps={compositionProps}
          durationInFrames={1}
          frameToDisplay={0}
          fps={fps}
          compositionWidth={roundedWidth}
          compositionHeight={roundedHeight}
          style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
          renderLoading={() => null}
        />
      )}
    </div>
  );
};

export default RemotionOverlayRenderer;
